<template>
	<div class="c-admin-email-chart">
		<p class="c-admin-email-chart__detail" role="status">
			<template v-if="activePoint">
				<strong>{{ activePoint.date }} (UTC)</strong>:
				{{ activePoint.day4Count }} Day 4, {{ activePoint.day7Count }} Day 7,
				{{ activePoint.offerCount }} offer emails
			</template>
			<template v-else>Hover, tap, or focus a day to see its counts.</template>
		</p>
		<div class="c-admin-email-chart__legend">
			<span class="c-admin-email-chart__day4">Day 4</span>
			<span class="c-admin-email-chart__day7">Day 7</span>
			<span class="c-admin-email-chart__offer">Offer emails</span>
		</div>
		<div class="c-admin-email-chart__scroll">
			<svg
				viewBox="0 0 720 300"
				role="group"
				aria-label="Workflow emails sent by date"
			>
				<title>
					{{ cumulative ? 'Cumulative' : 'Daily' }} workflow emails sent by date
				</title>
				<g v-for="tick in ticks" :key="tick.value">
					<line x1="48" x2="700" :y1="tick.y" :y2="tick.y" class="grid" />
					<text x="40" :y="tick.y + 4" text-anchor="end">{{ tick.value }}</text>
				</g>
				<polyline :points="day4Line" class="series day4" />
				<polyline :points="day7Line" class="series day7" />
				<polyline :points="offerLine" class="series offer" />
				<g v-for="point in dateLabels" :key="point.date">
					<text :x="point.x" y="282" :text-anchor="point.anchor">
						{{ point.date.slice(5) }}
					</text>
				</g>
				<g v-if="activePoint">
					<circle
						:cx="activePoint.x"
						:cy="activePoint.day4Y"
						r="4"
						class="point day4"
					/>
					<circle
						:cx="activePoint.x"
						:cy="activePoint.day7Y"
						r="4"
						class="point day7"
					/>
					<circle
						:cx="activePoint.x"
						:cy="activePoint.offerY"
						r="4"
						class="point offer"
					/>
				</g>
				<rect
					v-for="(point, index) in points"
					:key="point.date"
					:x="point.x - pointSpacing / 2"
					y="16"
					:width="pointSpacing"
					height="244"
					class="hit-area"
					tabindex="0"
					:aria-label="`${point.date} UTC: ${point.day4Count} Day 4, ${point.day7Count} Day 7, ${point.offerCount} offer emails`"
					@mouseenter="activeIndex = index"
					@mouseleave="activeIndex = null"
					@focus="activeIndex = index"
					@blur="activeIndex = null"
					@click="activeIndex = index"
				>
					<title>
						{{ point.date }} UTC: {{ point.day4Count }} Day 4,
						{{ point.day7Count }} Day 7, {{ point.offerCount }} offer emails
					</title>
				</rect>
			</svg>
		</div>
	</div>
</template>

<script>
export default {
	name: 'AdminEmailChart',
	props: {
		days: { type: Array, required: true },
		cumulative: { type: Boolean, default: false },
	},
	data() {
		return { activeIndex: null };
	},
	computed: {
		displayDays() {
			let day4Total = 0;
			let day7Total = 0;
			let offerTotal = 0;
			return this.days.map((day) => {
				day4Total += day.day4Count;
				day7Total += day.day7Count;
				offerTotal += day.offerCount;
				return {
					date: day.date,
					day4Count: this.cumulative ? day4Total : day.day4Count,
					day7Count: this.cumulative ? day7Total : day.day7Count,
					offerCount: this.cumulative ? offerTotal : day.offerCount,
				};
			});
		},
		step() {
			let maximum = 0;
			for (const day of this.displayDays) {
				maximum = Math.max(
					maximum,
					day.day4Count,
					day.day7Count,
					day.offerCount
				);
			}
			return Math.max(1, Math.ceil(maximum / 4));
		},
		ticks() {
			const ticks = [];
			for (let i = 0; i <= 4; i++) {
				ticks.push({ value: i * this.step, y: 260 - i * 60 });
			}
			return ticks;
		},
		pointSpacing() {
			return 652 / Math.max(1, this.days.length - 1);
		},
		points() {
			return this.displayDays.map((day, index) => {
				return {
					date: day.date,
					day4Count: day.day4Count,
					day7Count: day.day7Count,
					offerCount: day.offerCount,
					x: 48 + index * this.pointSpacing,
					day4Y: 260 - (day.day4Count / (this.step * 4)) * 240,
					day7Y: 260 - (day.day7Count / (this.step * 4)) * 240,
					offerY: 260 - (day.offerCount / (this.step * 4)) * 240,
				};
			});
		},
		day4Line() {
			return this.points.map((point) => `${point.x},${point.day4Y}`).join(' ');
		},
		day7Line() {
			return this.points.map((point) => `${point.x},${point.day7Y}`).join(' ');
		},
		offerLine() {
			return this.points.map((point) => `${point.x},${point.offerY}`).join(' ');
		},
		dateLabels() {
			const interval = Math.max(1, Math.ceil((this.points.length - 1) / 4));
			return this.points
				.filter(
					(point, index) =>
						index % interval === 0 || index === this.points.length - 1
				)
				.map((point) => ({
					date: point.date,
					x: point.x,
					anchor: point.x === 48 ? 'start' : point.x === 700 ? 'end' : 'middle',
				}));
		},
		activePoint() {
			return this.activeIndex === null ? null : this.points[this.activeIndex];
		},
	},
};
</script>

<style lang="scss">
.c-admin-email-chart {
	&__detail {
		min-height: 2.5em;
		margin: var(--margin-lg) 0 0;
		font-size: var(--font-size-sm);
	}

	&__legend {
		display: flex;
		flex-wrap: wrap;
		gap: var(--margin-lg);
		font-size: var(--font-size-sm);
	}

	&__legend span::before {
		content: '';
		display: inline-block;
		width: 1rem;
		height: 2px;
		margin-right: var(--margin-sm);
		vertical-align: middle;
	}

	&__day4::before {
		background: var(--color-primary);
	}

	&__day7::before {
		background: var(--color-secondary);
	}

	&__offer::before {
		background: var(--color-warning);
	}

	&__scroll {
		overflow-x: auto;
	}

	svg {
		display: block;
		width: 100%;
		min-width: 520px;
	}

	text {
		fill: var(--color-font);
		font-size: 12px;
	}

	.grid {
		stroke: var(--color-bg-5);
		stroke-width: 1;
	}

	.series {
		fill: none;
		stroke-width: 2;
		vector-effect: non-scaling-stroke;
	}

	.day4 {
		stroke: var(--color-primary);
	}

	.day7 {
		stroke: var(--color-secondary);
	}

	.offer {
		stroke: var(--color-warning);
	}

	.point.day4 {
		fill: var(--color-primary);
	}

	.point.day7 {
		fill: var(--color-secondary);
	}

	.point.offer {
		fill: var(--color-warning);
	}

	.hit-area {
		fill: transparent;
		cursor: crosshair;

		&:focus {
			outline: none;
			stroke: var(--color-primary);
			stroke-width: 1;
		}
	}
}
</style>
